import {
	ChangeDetectionStrategy, ChangeDetectorRef,
	Component, DestroyRef,
	EventEmitter, inject,
	model,
	OnInit
} from '@angular/core';
import {
	AlarisDialogService,
	AlarisEditPanelService,
	AlarisFilterManagerComponent,
	EditPanelWidth,
	LocalTableUtils,
	PageEvent,
	TableFiltersIndicator, TableRefBooksIndicator,
	TableSimple,
	TableWithFilter
} from '@campaign-portal/components-library';
import { MDTask, MDTaskResult } from '@helpers/types/models';
import { ManualDippingErrorsTableService } from './manual-dipping-errors-table.service';
import { ActivatedRoute } from '@angular/router';
import { 
	ManualDippingErrorsTableFieldsService, 
	MDRESULTS_ERROR_TABLE_FILTERS 
} from './manual-dipping-errors-table-fields.service';
import { ViewErrorLogComponent } from './view-error-log/view-error-log.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize } from 'rxjs';
import { ManualDippingResultsTableService } from '../manual-dipping-results-table/manual-dipping-results-table.service';

@Component({
	selector: 'app-manual-dipping-errors-table',
	standalone: false,
	templateUrl: './manual-dipping-errors-table.component.html',
	styleUrls: [
		'./manual-dipping-errors-table.component.scss',
		// eslint-disable-next-line max-len
		'../../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManualDippingErrorsTableComponent implements OnInit, TableSimple<MDTaskResult>, TableWithFilter {
	readonly exporting$ = new BehaviorSubject<boolean>(false);

	mdTaskID!: number | null;
	mdTask!: MDTask | undefined;

	tHeaders = model(LocalTableUtils.toTableFields(this.fieldsService.headers));
	tRows: MDTaskResult[] = [];
	filters = model<TableFiltersIndicator>(MDRESULTS_ERROR_TABLE_FILTERS);
	readonly tFiltersChange = new EventEmitter<TableFiltersIndicator>();

	total = 0;
	activePage = 0;
	pageSize = 25;

	readonly pageSizeOptions = [25, 50, 100];

	private destroyRef = inject(DestroyRef);

	constructor(
        readonly service: ManualDippingErrorsTableService,
		readonly results: ManualDippingResultsTableService,
		private route: ActivatedRoute,
        private readonly fieldsService: ManualDippingErrorsTableFieldsService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly editPanel: AlarisEditPanelService,
        private readonly dialog: AlarisDialogService,
	) { }

	ngOnInit(): void {
		this.mdTaskID = Number(this.route.snapshot.paramMap.get('id'));
		this.load();
		this.results.loadTask(this.mdTaskID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
			this.mdTask = res;
			this.setRefBooksInFields({
				sourceName: { list$: new BehaviorSubject<string[]>(res.sources) }
			});
			this.cdRef.markForCheck();
		})
	}

	confirm(): void {
		if (this.mdTask && this.mdTask.mdTaskID !== null) {
			this.exporting$.next(true);
			this.results
				.export(this.mdTask.mdTaskID)
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					finalize(() => this.exporting$.next(false))
				)
				.subscribe();
		}
	}

	load(): void {
		this.service.pageParams.limit = this.pageSize;
		this.service.pageParams.activePage = this.activePage;
		if (this.mdTaskID !== null && this.mdTaskID !== undefined) {
			this.service.loadResult(this.mdTaskID)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(res => {
					if (res.data && res.length) {
						this.total = res.length;
						this.tRows = res.data;
						this.cdRef.markForCheck();
					}
				})
		}
	}

	paginationChange(event: PageEvent): void {
		this.pageSize = event.pageSize;
		this.activePage = event.pageIndex;
		this.saveState();
		this.load();
	}

	private saveState(): void {
		this.service.pageParams.limit = this.pageSize;
	}

	applyFilter($event: TableFiltersIndicator): void {
		this.service.filters = [];
		$event.forEach((value, key) => {
			if (value.value !== undefined && key && value.value !== null) {
				this.service.filters.push({
					key,
					value: value.value
				})
			}
		});
		this.load();
	}

	get isFiltersApplied(): boolean {
		return this.service.filters.length > 0;
	}

	editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.SM, {
			tHeaders: this.tHeaders(),
			filters: this.filters,
			filtersChange: this.tFiltersChange,
		});
	}

	viewLog(log: string): void {
		this.dialog.open<boolean>(ViewErrorLogComponent, { autoFocus: false, data: { log } })
	}

	private setRefBooksInFields(refBooks: TableRefBooksIndicator): void {
		this.tHeaders().forEach((field) => {
			const refBook = refBooks[field.variable];
			if (refBook) {
				Object.assign(field, { data$: refBook.list$, loading$: refBook.loading$ });
			}
		});
	}
}
